import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Copy, Check, ExternalLink } from "lucide-react"
import hive from "@/lib/api/hive"
import type { CreateFireShareInput } from "@/types/fire"
import { useAuth } from "@/context/auth/auth-context"
import { Analytics } from "@/lib/analytics"

interface FireShareDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	progressPct: number
	freedomYear: number | null
	yearsToFreedom: number | null
	positionCount: number
}

export function FireShareDialog({
	open,
	onOpenChange,
	progressPct,
	freedomYear,
	yearsToFreedom,
	positionCount,
}: FireShareDialogProps) {
	const { token } = useAuth()
	const [shareUrl, setShareUrl] = useState<string | null>(null)
	const [creating, setCreating] = useState(false)
	const [copied, setCopied] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleCreate = async () => {
		if (!token) return
		setCreating(true)
		setError(null)
		try {
			const input: CreateFireShareInput = {
				progress_pct: Math.min(Math.round(progressPct), 100),
				freedom_year: freedomYear,
				years_to_freedom: yearsToFreedom,
				position_count: positionCount,
			}
			const share = await hive.createFireShare(input, token)
			setShareUrl(`https://api.beediv.com/v1/fire/share/${share.id}`)
			Analytics.capture("FIRE Progress Shared", { progress_pct: input.progress_pct })
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to create share link")
		} finally {
			setCreating(false)
		}
	}

	const handleCopy = async () => {
		if (!shareUrl) return
		await navigator.clipboard.writeText(shareUrl)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	const handleReddit = () => {
		if (!shareUrl) return
		const title = encodeURIComponent(`${progressPct}% toward FIRE — tracking with BeeDiv`)
		window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${title}`, "_blank")
	}

	const handleTwitter = () => {
		if (!shareUrl) return
		const text = encodeURIComponent(
			`I'm ${progressPct}% toward financial independence! Tracking ${positionCount} dividend positions with @BeeDiv`,
		)
		window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, "_blank")
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Share Your FIRE Progress</DialogTitle>
				</DialogHeader>

				<div className="space-y-4">
					{/* Preview card */}
					<div className="bg-slate-900 dark:bg-black/40 border border-amber-400/30 rounded-lg p-4 space-y-2">
						<div className="text-2xl font-bold text-amber-400">{progressPct}%</div>
						<div className="text-sm text-slate-400">toward financial independence</div>
						<div className="bg-slate-700 rounded-full h-2 overflow-hidden">
							<div
								className="h-full rounded-full"
								style={{
									width: `${Math.min(progressPct, 100)}%`,
									background: "linear-gradient(90deg,#f59e0b,#fbbf24)",
								}}
							/>
						</div>
						{freedomYear && <div className="text-xs text-slate-400">Freedom year: {freedomYear}</div>}
						{!freedomYear && yearsToFreedom && (
							<div className="text-xs text-slate-400">{yearsToFreedom} years to freedom</div>
						)}
						<div className="text-xs text-slate-500">Tracking {positionCount} dividend positions</div>
					</div>

					<p className="text-xs text-muted-foreground">
						Only percentages and timeline are shared. No dollar amounts.
					</p>

					{error && <p className="text-sm text-destructive">{error}</p>}

					{!shareUrl ? (
						<Button
							onClick={handleCreate}
							disabled={creating}
							className="w-full bg-sky-500 hover:bg-sky-600 text-white"
						>
							{creating ? "Creating link..." : "Create Share Link"}
						</Button>
					) : (
						<div className="space-y-3">
							<div className="flex gap-2">
								<Input value={shareUrl} readOnly className="text-xs" />
								<Button variant="outline" size="icon" onClick={handleCopy}>
									{copied ? (
										<Check className="h-4 w-4 text-green-500" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</Button>
							</div>
							<div className="flex gap-2">
								<Button variant="outline" size="sm" onClick={handleReddit} className="flex-1 gap-1">
									<ExternalLink className="h-3 w-3" /> Reddit
								</Button>
								<Button variant="outline" size="sm" onClick={handleTwitter} className="flex-1 gap-1">
									<ExternalLink className="h-3 w-3" /> Twitter/X
								</Button>
							</div>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
